import { useEffect, useState } from "react";
import styles from "./Registration.module.css";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../../config/api";
type Country = {
  name: { common: string };
  idd?: { root?: string; suffixes?: string[] };
};
const passwordRules = {
  length: (pw: string) => pw.length >= 8,
  uppercase: (pw: string) => /[A-Z]/.test(pw),
  lowercase: (pw: string) => /[a-z]/.test(pw),
  number: (pw: string) => /[0-9]/.test(pw),
  special: (pw: string) => /[^A-Za-z0-9]/.test(pw),
};
export default function Registration() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [customerType, setCustomerType] = useState<"B2C" | "B2B">("B2C");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [passwordTouched, setPasswordTouched] = useState(false);



  const [formData, setFormData] = useState({
    customerType: "B2C",

    salutation: "",
    firstName: "",
    lastName: "",
    birthdate: "",

    street: "",
    houseNumber: "",
    postalCode: "",
    city: "",
    federalState: "",
    country: "",

    phoneCountryCode: "",
    phone: "",

    email: "",
    password: "",
    passwordRepeat: "",

    companyName: "",
    vatId: "",
    contactFirstName: "",
    contactLastName: "",

    agbAccepted: false,
    privacyAccepted: false,
    emailNotifications: false,
  });

  const passwordValidation = {
  length: passwordRules.length(formData.password),
  uppercase: passwordRules.uppercase(formData.password),
  lowercase: passwordRules.lowercase(formData.password),
  number: passwordRules.number(formData.password),
  special: passwordRules.special(formData.password),
};

const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const navigate = useNavigate();
  /* 🌍 Load countries */
  useEffect(() => {
    fetch("https://restcountries.com/v3.1/all?fields=name,idd")
      .then(res => res.json())
      .then(data =>
        setCountries(
          data.sort((a: Country, b: Country) =>
            a.name.common.localeCompare(b.name.common)
          )
        )
      );
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as any;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (formData.password !== formData.passwordRepeat) {
      setError("Passwords do not match");
      return;
    }

    if (!formData.agbAccepted || !formData.privacyAccepted) {
      setError("You must accept Terms & Privacy Policy");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        API_BASE + "/api/register/newUser",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            customerType,
            phone: `${formData.phoneCountryCode}${formData.phone}`,
          }),
        }
      );

      const result = await response.json();

        if (!response.ok) throw new Error(result.error || "Registration failed");

    // 2️⃣ Verifizierungs-E-Mail senden
    const verificationResponse = await fetch(API_BASE + "/api/account-verification", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: formData.email }),
    });

    if (!verificationResponse.ok) {
      const verificationResult = await verificationResponse.json();
      throw new Error(verificationResult.error || "Verification email failed");
    }

    if (!isPasswordValid) {
  setError("Password does not meet the minimum security requirements");
  return;
}

      if (!response.ok) throw new Error(result.error);
    //  alert("Registration successful. Please check your email.");
       navigate("/login");
    } catch (err: any) {
      setError(err.message || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <div className={styles.container}>
    <form className={styles.form} onSubmit={handleSubmit}>
  <h2 className={styles.title}>Create Account</h2>

  {/* Tabs */}
  <div className={styles.row}>
    <button type="button" onClick={() => setCustomerType("B2C")}>
      Private
    </button>
    <button type="button" onClick={() => setCustomerType("B2B")}>
      Business
    </button>
  </div>

  {error && <p className={styles.error}>{error}</p>}

  {/* 🌟 B2B: Company Name & VAT ID oben */}
  {customerType === "B2B" && (
    <>
      <input
        name="companyName"
        placeholder="Company name"
        required
        onChange={handleChange}
      />
      <input
        name="vatId"
        placeholder="VAT ID"
        required
        onChange={handleChange}
      />

      {/* Contact Salutation + Name */}
      <select name="salutation" required onChange={handleChange}>
        <option value="">Salutation</option>
        <option value="Mr">Mr</option>
        <option value="Ms">Ms</option>
      </select>
      <input
        name="contactFirstName"
        placeholder="Contact first name"
        required
        onChange={handleChange}
      />
      <input
        name="contactLastName"
        placeholder="Contact last name"
        required
        onChange={handleChange}
      />
    </>
  )}

  {/* 🌟 B2C: Private User */}
  {customerType === "B2C" && (
    <>
      <select name="salutation" required onChange={handleChange}>
        <option value="">Salutation</option>
        <option value="Mr">Mr</option>
        <option value="Ms">Ms</option>
      </select>

      <input
        name="firstName"
        placeholder="First name"
        required
        onChange={handleChange}
      />
      <input
        name="lastName"
        placeholder="Last name"
        required
        onChange={handleChange}
      />
      <input
        name="birthdate"
        type="date"
        onChange={handleChange}
      />
    </>
  )}

  {/* Address */}
  <input name="street" placeholder="Street" required onChange={handleChange} />
  <input name="houseNumber" placeholder="House number" required onChange={handleChange} />
  <input name="postalCode" placeholder="Postal code" required onChange={handleChange} />
  <input name="city" placeholder="City" required onChange={handleChange} />
  <input name="federalState" placeholder="State / Region" required onChange={handleChange} />

  <select name="country" required onChange={handleChange}>
    <option value="">Country</option>
    {countries.map(c => (
      <option key={c.name.common} value={c.name.common}>
        {c.name.common}
      </option>
    ))}
  </select>

  {/* Phone */}
  <div className={styles.row}>
    <select name="phoneCountryCode" onChange={handleChange}>
      <option value="">Code</option>
      {countries
        .filter(c => c.idd?.root)
        .map(c => (
          <option key={c.name.common} value={`${c.idd?.root}${c.idd?.suffixes?.[0] || ""}`}>
            {c.idd?.root}{c.idd?.suffixes?.[0]} ({c.name.common})
          </option>
        ))}
    </select>
    <input name="phone" placeholder="Phone number" onChange={handleChange} />
  </div>

  {/* Email & Password */}
  <input name="email" type="email" placeholder="Email" required onChange={handleChange} />
  <input name="password" type="password" placeholder="Password" required onChange={handleChange}   onBlur={() => setPasswordTouched(true)} />
  <input name="passwordRepeat" type="password" placeholder="Repeat password" required onChange={handleChange} />

  {passwordTouched && (
  <ul className={styles.passwordRules}>
    <li className={passwordValidation.length ? styles.ok : styles.error}>
      • At least 8 characters
    </li>
    <li className={passwordValidation.uppercase ? styles.ok : styles.error}>
      • At least one uppercase letter
    </li>
    <li className={passwordValidation.lowercase ? styles.ok : styles.error}>
      • At least one lowercase letter
    </li>
    <li className={passwordValidation.number ? styles.ok : styles.error}>
      • At least one number
    </li>
    <li className={passwordValidation.special ? styles.ok : styles.error}>
      • At least one special character
    </li>
  </ul>
)}

  {/* Terms & Privacy */}
  <label>
    <input type="checkbox" name="agbAccepted" onChange={handleChange} /> I accept the{" "}
    <a href="/termsCondition" target="_blank">Terms & Conditions</a>
  </label>

  <label>
    <input type="checkbox" name="privacyAccepted" onChange={handleChange} /> I accept the{" "}
    <a href="/privacyPolicy" target="_blank">Privacy Policy</a>
  </label>
           
  <label>
    <input type="checkbox" name="emailNotifications" onChange={handleChange} /> Email notifications
  </label>

  <button className={styles.submitButton} disabled={isLoading}>
    {isLoading ? "Registering..." : "Register"}
  </button>
</form>
</div>
  )
}